import type { SignupAnalyticsEventPayload } from "gsintegrations";
import { trackMetaEvent } from "./metaPixel";
import { capture, initPostHog } from "./posthog";

export type SignupFlowSource =
  | "direct"
  | "qr"
  | "invite"
  | "landing"
  | "internal_navigation";

export type SignupAnalyticsContext = {
  source: SignupFlowSource;
  return_to?: string;
  invite_code?: string;
  utm_source?: string;
  utm_campaign?: string;
  variant?: string;
  qr_session_id?: string;
  page_url?: string;
  context_timestamp: string;
};

export type SignupAppEventName =
  | "signup_redirect_to_onboarding"
  | "signup_onboarding_submit_attempt"
  | "signup_success"
  | "signup_error"
  | "signup_redirect_to_home"
  | "signup_abandon_after_success"
  | "signup_method_selected"
  | "signup_google_started"
  | "signup_google_success"
  | "signup_google_error"
  | "signup_google_existing_user_login"
  | "signup_facebook_started"
  | "signup_facebook_success"
  | "signup_facebook_error"
  | "signup_facebook_existing_user_login"
  | "onboarding_form_chat_started"
  | "onboarding_form_chat_field_collected"
  | "onboarding_form_chat_completed"
  | "onboarding_form_chat_submitted"
  | "onboarding_form_chat_error"
  | "onboarding_chat_bot_started"
  | "onboarding_chat_bot_field_collected"
  | "onboarding_chat_bot_completed"
  | "onboarding_chat_bot_submitted"
  | "onboarding_chat_bot_error";

type SignupCaptureProperties = SignupAnalyticsContext &
  Record<string, unknown> & {
    flow: "signup";
  };

const COMPLETE_REGISTRATION_EVENTS = new Set<string>([
  "signup_success",
  "signup_google_success",
  "signup_facebook_success",
]);

const buildProperties = (
  context: SignupAnalyticsContext,
  eventProperties?: Record<string, unknown>,
): SignupCaptureProperties => ({
  flow: "signup",
  ...context,
  ...(eventProperties ?? {}),
});

const maybeTrackMetaCompleteRegistration = (eventName: string): void => {
  if (!COMPLETE_REGISTRATION_EVENTS.has(eventName)) return;
  trackMetaEvent("CompleteRegistration");
};

export const trackSignupEvent = (
  payloadOrEvent: SignupAnalyticsEventPayload | SignupAppEventName,
  context: SignupAnalyticsContext,
  eventProperties?: Record<string, unknown>,
): void => {
  initPostHog();

  if (typeof payloadOrEvent === "string") {
    capture(payloadOrEvent, buildProperties(context, eventProperties));
    maybeTrackMetaCompleteRegistration(payloadOrEvent);
    return;
  }

  capture(
    payloadOrEvent.event,
    buildProperties(context, {
      ...payloadOrEvent,
      ...(eventProperties ?? {}),
    }),
  );
  maybeTrackMetaCompleteRegistration(payloadOrEvent.event);
};
